import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Tabs } from "@shared/ui/tabs";
import { GenericTable } from "@shared/components/GenericTable";
import { Checkbox } from "@shared/ui/checkbox";
import {
  CheckCircle,
  XCircle,
  FileText,
  ArrowLeft,
  Clock,
  DollarSign,
  Activity,
  User,
  Zap,
  Settings,
  Phone,
  Mail,
  Camera,
  PenTool,
  Loader2,
  AlertCircle as AlertCircleIcon,
  CheckSquare,
  Package,
  Wrench,
  HelpCircle,
  ExternalLink,
  Building,
} from "lucide-react";

import {
  useAcceptJobCard,
  useJobCardDetail,
  useJobCardMaterialReport,
  useJobCardReadingsReport,
  useJobCardTaskReport,
  useRejectJobCard,
  useJobCardSignatureReport,
  useDownloadReport,
} from "@features/wx/dispatcher/hooks";
import { wxUtils, getStatusColor } from "@features/wx/utils";
import CompletedServiceOrderKPI from "./dashboard/dashboard";

// Configuration constants
const CONFIG = {
  pagination: {
    itemsPerPage: 5,
    defaultPage: 1,
  },
  routes: {
    home: "/",
    serviceManagement: "/service-management",
    completedOrders: "/completed-orders",
    dispatcher: "/service-orders/dispatcher?page=1&tab=completed",
  },
  defaultValues: {
    cost: "0",
    text: "N/A",
    quantity: 0,
    reading: "0",
  },
  tabs: {
    main: {
      details: "details",
      reports: "reports",
    },
    consumer: {
      consumerMeter: "consumer-meter",
      technician: "technician",
    },
    details: {
      overview: "overview",
      tasks: "tasks",
      materials: "materials",
      readings: "readings",
      help: "help",
    },
    reports: {
      overview: "overview",
      tasks: "tasks",
      materials: "materials",
      readings: "readings",
      signature: "signature",
      technicians: "technicians",
    },
  },
  badges: {
    resourceTypes: {
      Document: "bg-blue-100 text-blue-800",
      instruction: "bg-blue-100 text-blue-800",
      "Video Link": "bg-red-100 text-red-800",
      Checklist: "bg-green-100 text-green-800",
      default: "bg-gray-100 text-gray-800",
    },
  },
  colors: {
    costs: {
      estimated: "text-blue-600",
      actual: "text-green-600",
    },
    status: {
      positive: "text-green-600",
      warning: "text-orange-600",
      neutral: "text-gray-700",
    },
  },
};
interface Document {
  id: number;
  type: number;
  typeDisplay: string;
  sop: number;
  remoteUtilityId: number;
  file: string | null;
  link: string | null;
  description: string;
  isActive: boolean;
}
// Utility functions
const formatCurrency = (value, currency = "$") => {
  const amount = value || CONFIG.defaultValues.cost;
  return `${currency}${amount}`;
};

const getResourceTypeBadgeClass = (resourceType) => {
  return (
    CONFIG.badges.resourceTypes[resourceType] ||
    CONFIG.badges.resourceTypes.default
  );
};

const AssetCompletedServiceOrderViewPage = () => {
  const { id, serviceType } = useParams();
  const navigate = useNavigate();
  const [activeMainTab, setActiveMainTab] = useState(CONFIG.tabs.main.details);
  const [activeConsumerTab, setActiveConsumerTab] = useState(
    CONFIG.tabs.consumer.consumerMeter
  );
  const [currentPage, setCurrentPage] = useState(CONFIG.pagination.defaultPage);

  // ✅ KEEP: Existing job card detail data (for Details tab)
  const {
    data: jobCardResponse,
    isLoading: loading,
    error,
    refetch,
  } = useJobCardDetail({ id });

  // ✅ NEW: Report data hooks (only for Reports tab)
  const {
    data: taskReportData,
    isLoading: taskLoading,
    error: taskError,
  } = useJobCardTaskReport(id, { enabled: !!id });

  const {
    data: materialReportData,
    isLoading: materialLoading,
    error: materialError,
  } = useJobCardMaterialReport(id, { enabled: !!id });

  const {
    data: readingsReportData,
    isLoading: readingsLoading,
    error: readingsError,
  } = useJobCardReadingsReport(id, { enabled: !!id });
  const {
    data: signatureReportData,
    isLoading: signatureLoading,
    error: signatureError,
  } = useJobCardSignatureReport(id, { enabled: !!id });

  const rejectJobCard = useRejectJobCard();
  const acceptJobCard = useAcceptJobCard();
  const downloadCSV = useDownloadReport();

  // ✅ KEEP: Existing data transformation (for Details tab)
  const transformedData = useMemo(() => {
    if (!jobCardResponse) return null;
    return wxUtils.transformJobCardDetail(jobCardResponse);
  }, [jobCardResponse]);

  // ✅ NEW: Transform report data (only for Reports tab)
  const transformedReportData = useMemo(() => {
    const tasks =
      taskReportData?.result?.task?.map((task, index) => {
        return {
          taskId: index,
          taskName: task.name,
          tatMins: task.time,
          downtimeMins: parseInt(task.downtime) || 0,
          photoRequired: task.isFile,
          isComplete: task.isComplete,
          status: task.status,
          taskFile: task.taskFile,
          photo: task.taskFile,
          executed: task.isComplete,
        };
      }) || [];

    const materials =
      materialReportData?.result?.material?.map((material, index) => {
        return {
          materialId: index,
          name: material.name || "N/A",
          size: material.size || "N/A",
          quantity: material.quantity || "0",
          actualQuantity: material.actualQuantity || material.quantity || "0",
          cost: material.cost || "0",
          unit: material.unit || "pcs",
        };
      }) || [];

    const readings =
      readingsReportData?.result?.reading?.map((reading, index) => {
        return {
          readingId: index,
          key: reading.key,
          name: reading.name,
          unit: reading.unit,
          minimum: reading.minimum,
          maximum: reading.maximum,
          range: `${reading.minimum}-${reading.maximum}`,
          reading: reading.readings,
          readingFile: reading.readingFile,
          photoRequired: !!reading.readingFile,
        };
      }) || [];

    return { tasks, materials, readings };
  }, [taskReportData, materialReportData, readingsReportData]);

  // ✅ KEEP: Extract existing data for Details tab
  const {
    serviceOrderData,
    consumerDetails,
    meterDetails,
    assetDetails,
    technicianDetails,
    jobOverview,
    tasksData, // ← Keep for Details tab
    materialsData, // ← Keep for Details tab
    readingsData, // ← Keep for Details tab
    helpResourcesData,
  } = transformedData || {};

  // Service type configuration - ADD THIS ENTIRE BLOCK
  const serviceTypeConfig = {
    Asset: {
      title: "Asset Details",
      icon: "🏗️",
      fields: {
        id: "Asset ID",
        name: "Asset Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    Facility: {
      title: "Facility Details",
      icon: "🏢",
      fields: {
        id: "Facility ID",
        name: "Facility Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    System: {
      title: "System Details",
      icon: "⚙️",
      fields: {
        id: "System ID",
        name: "System Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    Network: {
      title: "Network Details",
      icon: "🌐",
      fields: {
        id: "Network ID",
        name: "Network Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
  };

  // DetailRow component - ADD THIS
  const DetailRow = ({ label, value }) => (
    <div>
      <label className="text-sm font-medium text-gray-500">{label}</label>
      <p className="text-base text-gray-900">{value || "N/A"}</p>
    </div>
  );

  // Add after useParams destructuring
  const validServiceType =
    serviceType && serviceType in serviceTypeConfig ? serviceType : "Asset";
  const currentServiceConfig = serviceTypeConfig[validServiceType];

  // ✅ NEW: Use report data for Reports tab
  const {
    tasks: reportTasks,
    materials: reportMaterials,
    readings: reportReadings,
  } = transformedReportData;

  // Dynamic Service & Technician tab components
  const serviceTechnicianTabComponents = useMemo(
    () => ({
      "service-details": {
        label: currentServiceConfig.title,
        icon: <Building className="h-4 w-4" />,
        component: (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <DetailRow
                label={currentServiceConfig.fields.id}
                value={assetDetails?.assetId}
              />
              <DetailRow
                label={currentServiceConfig.fields.name}
                value={assetDetails?.assetName}
              />
            </div>
            <DetailRow
              label={currentServiceConfig.fields.location}
              value={assetDetails?.location}
            />
            <div className="grid grid-cols-2 gap-4">
              <DetailRow
                label={currentServiceConfig.fields.condition}
                value={assetDetails?.condition}
              />
              <DetailRow
                label={currentServiceConfig.fields.riskScore}
                value={assetDetails?.rpn}
              />
            </div>
            <DetailRow
              label={currentServiceConfig.fields.status}
              value={assetDetails?.status}
            />
          </div>
        ),
      },
      technician: {
        label: "Assigned Technician",
        icon: <User className="h-4 w-4" />,
        component: (
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-green-600" />
              Assignee Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Technician Name
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.technicianName || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  FID
                </label>
                <div className="text-gray-900 font-mono">
                  {technicianDetails?.fid || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Phone
                </label>
                <div className="flex items-center gap-2 text-gray-900">
                  <Phone className="h-4 w-4 text-gray-500" />
                  {technicianDetails?.phone || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Role
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.role?.name ||
                    technicianDetails?.role ||
                    "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.email || "N/A"}
                </div>
              </div>
            </div>
          </div>
        ),
      },
    }),
    [currentServiceConfig, assetDetails, technicianDetails]
  );

  // Pagination helpers
  const createPaginationConfig = (data = []) => {
    const totalItems = data.length;
    const totalPages = Math.max(
      1,
      Math.ceil(totalItems / CONFIG.pagination.itemsPerPage)
    );
    const safePage = Math.max(1, Math.min(currentPage, totalPages));

    return {
      currentPage: safePage,
      totalPages,
      totalItems,
      itemsPerPage: CONFIG.pagination.itemsPerPage,
      startItem:
        totalItems === 0
          ? 0
          : (safePage - 1) * CONFIG.pagination.itemsPerPage + 1,
      endItem:
        totalItems === 0
          ? 0
          : Math.min(safePage * CONFIG.pagination.itemsPerPage, totalItems),
    };
  };

  const getPaginatedData = (data = []) => {
    if (!Array.isArray(data)) return [];
    const startIndex = (currentPage - 1) * CONFIG.pagination.itemsPerPage;
    return data.slice(startIndex, startIndex + CONFIG.pagination.itemsPerPage);
  };

  const renderDocumentLink = (document: Document) => {
    console.log("debug document", document);
    if (document.file) {
      return (
        <a
          href={document.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FileText className="h-4 w-4 mr-1" />
          View File
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    if (document.link) {
      return (
        <a
          href={document.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Open Link
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    return <span className="text-muted-foreground">No link/file</span>;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleApprove = () => {
    acceptJobCard
      .mutateAsync({
        jobCardId: parseInt(id),
      })
      .then(handleBack);
  };

  const handleDownload = () => {
    if (!id) return;
    downloadCSV.mutateAsync({
      reportId: Number(id),
      tabName: "completed",
    });
  };

  const handleReject = () => {
    rejectJobCard
      .mutateAsync({
        jobCardId: parseInt(id),
      })
      .then(handleBack);
  };

  const handleBack = () => {
    navigate(CONFIG.routes.dispatcher);
  };

  // ✅ KEEP: Details tab columns (unchanged)
  const tasksColumns = [
    {
      key: "taskName",
      header: "Task Name",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "tatMins",
      header: "TAT (Mins)",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.quantity}
        </span>
      ),
    },
    {
      key: "downtimeMins",
      header: "Downtime (Mins)",
      render: (value) => (
        <span
          className={
            value > 0
              ? CONFIG.colors.status.warning
              : CONFIG.colors.status.neutral
          }
        >
          {value || CONFIG.defaultValues.quantity}
        </span>
      ),
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      render: (value) => (
        <div className="text-center">
          {value ? (
            <Camera className="h-4 w-4 text-green-600 mx-auto" />
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
  ];

  const materialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "quantity",
      header: "Quantity",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.quantity}
        </span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      render: (value) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(value)}
        </span>
      ),
    },
  ];

  const readingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "range",
      header: "Range",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      render: (value) => (
        <div className="text-center">
          {value ? (
            <Camera className="h-4 w-4 text-green-600 mx-auto" />
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
  ];

  const helpResourcesColumns = [
    {
      key: "resourceName",
      header: "Resource Name",
      render: (value) => (
        <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "resourceType",
      header: "Resource Type",
      type: "badge" as const,
      badgeConfig: {
        getClassName: getResourceTypeBadgeClass,
      },
    },
    {
      key: "details",
      header: "Details",
      render: (value: string, row: Document) => renderDocumentLink(row),
    },
  ];

  const technicianColumns = [
    {
      key: "fid",
      header: "FID",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "technicianName",
      header: "Name",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
  ];

  // Reports table columns - keep same design as original but show actual data
  const reportsTasksColumns = [
    ...tasksColumns,
    {
      key: "photo",
      header: "Photo",
      render: (value, row) => (
        <div className="text-center">
          {row.taskFile ? (
            <img
              src={row.taskFile}
              alt="Task photo"
              className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
              onClick={() => window.open(row.taskFile, "_blank")}
            />
          ) : (
            <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
              <Camera className="h-3 w-3 text-gray-400" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: "executed",
      header: "Executed",
      render: (value, row) => (
        <div className="text-center">
          <Checkbox checked={row.isComplete} disabled />
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: () => (
        <div className="text-center">
          <Checkbox defaultChecked />
        </div>
      ),
    },
  ];

  const reportsMaterialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "quantity",
      header: "Est Quantity",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.quantity}
        </span>
      ),
    },
    {
      key: "actualQuantity",
      header: "Actual Used Quantity",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.quantity}
        </span>
      ),
    },
    {
      key: "cost",
      header: "Cost Per Unit",
      render: (value) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: () => (
        <div className="text-center">
          <Checkbox defaultChecked />
        </div>
      ),
    },
  ];

  const reportsReadingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value) => (
        <span className="font-medium text-gray-900">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "range",
      header: "Range",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "minimum",
      header: "Minimum",
      render: (value, row) => (
        <span className="text-gray-700">
          {row.range?.split("-")[0] || value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "maximum",
      header: "Maximum",
      render: (value, row) => (
        <span className="text-gray-700">
          {row.range?.split("-")[1] || value || CONFIG.defaultValues.text}
        </span>
      ),
    },
    {
      key: "reading",
      header: "Reading",
      render: (value) => (
        <span className="text-gray-700">
          {value || CONFIG.defaultValues.reading}
        </span>
      ),
    },
    {
      key: "photo",
      header: "Photo",
      render: (value, row) => (
        <div className="text-center">
          {row.readingFile ? (
            <img
              src={row.readingFile}
              alt="Reading photo"
              className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
              onClick={() => window.open(row.readingFile, "_blank")}
            />
          ) : (
            <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
              <Camera className="h-3 w-3 text-gray-400" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: () => (
        <div className="text-center">
          <Checkbox defaultChecked />
        </div>
      ),
    },
  ];

  // Cost overview component
  const CostOverview = ({ showComparison = false }) => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          {[
            { label: "Labour Cost", key: "labourCost" },
            { label: "Material Cost", key: "materialCost" },
            { label: "Other Cost", key: "otherCost" },
          ].map(({ label, key }) => (
            <div key={key} className="flex justify-between">
              <span className="text-gray-600">{label}:</span>
              {showComparison ? (
                <div className="flex gap-4">
                  <span className={CONFIG.colors.costs.estimated}>
                    {formatCurrency(jobOverview?.[key])} (Est)
                  </span>
                  <span className={CONFIG.colors.costs.actual}>
                    {formatCurrency(jobOverview?.[key])} (Actual)
                  </span>
                </div>
              ) : (
                <span className="font-medium">
                  {formatCurrency(jobOverview?.[key])}
                </span>
              )}
            </div>
          ))}
          <div className="flex justify-between border-t pt-2">
            <span className="text-gray-900 font-semibold">Total Cost:</span>
            {showComparison ? (
              <div className="flex gap-4">
                <span className={`${CONFIG.colors.costs.estimated} font-bold`}>
                  {formatCurrency(jobOverview?.totalCost)} (Est)
                </span>
                <span className={`${CONFIG.colors.costs.actual} font-bold`}>
                  {formatCurrency(jobOverview?.totalCost)} (Actual)
                </span>
              </div>
            ) : (
              <span className="font-bold">
                {formatCurrency(jobOverview?.totalCost)}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-3">
          {[
            { label: "Efforts", key: "efforts" },
            { label: "Downtime", key: "downtime" },
            { label: "Required Fieldforce", key: "requiredFieldforce" },
          ].map(({ label, key }) => (
            <div key={key} className="flex justify-between">
              <span className="text-gray-600">{label}:</span>
              {showComparison && (key === "efforts" || key === "downtime") ? (
                <div className="flex gap-4">
                  <span className={CONFIG.colors.costs.estimated}>
                    {jobOverview?.[key] || CONFIG.defaultValues.text} (Est)
                  </span>
                  <span className={CONFIG.colors.costs.actual}>
                    {jobOverview?.[key] || CONFIG.defaultValues.text} (Actual)
                  </span>
                </div>
              ) : (
                <span className="font-medium">
                  {jobOverview?.[key] || CONFIG.defaultValues.text}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Signature component
  const SignatureSection = () => {
    const signatureData = signatureReportData?.result;

    console.log("Signature data:", signatureData); // Debug log

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              title: "TPI Signature",
              type: "tpi",
              imageUrl: signatureData?.fieldForceSignature,
              isApproved: signatureData?.isFieldForceSignApproved,
            },
            {
              title: "Consumer Signature",
              type: "consumer",
              imageUrl: signatureData?.requestSignature,
              isApproved: signatureData?.isRequestSignApproved,
            },
          ].map(({ title, type, imageUrl, isApproved }) => (
            <div key={type}>
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <PenTool className="h-4 w-4 text-orange-600" />
                {title}
              </h4>

              {signatureLoading ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400 mr-2" />
                    <span className="text-gray-500">Loading signature...</span>
                  </div>
                </div>
              ) : imageUrl ? (
                <div className="border-2 border-solid border-gray-300 rounded-lg p-4 text-center bg-white">
                  <img
                    src={imageUrl}
                    alt={`${title}`}
                    className="max-w-full h-32 object-contain mx-auto cursor-pointer border rounded"
                    onClick={() => window.open(imageUrl, "_blank")}
                    onError={(e) => {}}
                  />
                  <div
                    style={{ display: "none" }}
                    className="text-red-500 mt-2"
                  >
                    <PenTool className="h-8 w-8 text-red-400 mx-auto mb-2" />
                    <p>Failed to load signature</p>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                  <PenTool className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No signature captured</p>
                  <div className="mt-3">
                    <Checkbox checked={false} disabled />
                    <span className="ml-2 text-sm text-gray-600">
                      Not signed
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {signatureData?.completedDate && (
          <div className="text-center text-sm text-gray-500 mt-4">
            Completed on:{" "}
            {new Date(signatureData.completedDate).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  // ✅ KEEP: Details tab components (unchanged)
  const detailsTabComponents = useMemo(
    () => ({
      [CONFIG.tabs.details.overview]: {
        label: "Overview",
        icon: <DollarSign className="h-4 w-4" />,
        component: <CostOverview />,
      },
      [CONFIG.tabs.details.tasks]: {
        label: "Tasks",
        icon: <CheckSquare className="h-4 w-4" />,
        count: tasksData?.length || 0, // ← Uses existing data
        component: (
          <GenericTable
            data={getPaginatedData(tasksData)} // ← Uses existing data
            columns={tasksColumns} // ← Your existing columns
            rowKey="taskId"
            pagination={createPaginationConfig(tasksData)}
            onPageChange={handlePageChange}
            emptyMessage="No tasks found"
          />
        ),
      },
      [CONFIG.tabs.details.materials]: {
        label: "Materials",
        icon: <Package className="h-4 w-4" />,
        count: materialsData?.length || 0, // ← Uses existing data
        component: (
          <GenericTable
            data={getPaginatedData(materialsData)} // ← Uses existing data
            columns={materialsColumns} // ← Your existing columns
            rowKey="materialId"
            pagination={createPaginationConfig(materialsData)}
            onPageChange={handlePageChange}
            emptyMessage="No materials found"
          />
        ),
      },
      [CONFIG.tabs.details.readings]: {
        label: "Readings",
        icon: <Wrench className="h-4 w-4" />,
        count: readingsData?.length || 0, // ← Uses existing data
        component: (
          <GenericTable
            data={getPaginatedData(readingsData)} // ← Uses existing data
            columns={readingsColumns} // ← Your existing columns
            rowKey="readingId"
            pagination={createPaginationConfig(readingsData)}
            onPageChange={handlePageChange}
            emptyMessage="No readings found"
          />
        ),
      },
      [CONFIG.tabs.details.help]: {
        label: "Help",
        icon: <HelpCircle className="h-4 w-4" />,
        count: helpResourcesData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(helpResourcesData)}
            columns={helpResourcesColumns}
            rowKey="resourceId"
            pagination={createPaginationConfig(helpResourcesData)}
            onPageChange={handlePageChange}
            emptyMessage="No help resources found"
          />
        ),
      },
    }),
    [
      currentPage,
      tasksData, // ← Existing data
      materialsData, // ← Existing data
      readingsData, // ← Existing data
      helpResourcesData,
      jobOverview,
    ]
  );

  // ✅ Reports tab components with report data but same design
  const reportsTabComponents = useMemo(
    () => ({
      [CONFIG.tabs.reports.overview]: {
        label: "Overview",
        icon: <DollarSign className="h-4 w-4" />,
        component: <CostOverview showComparison={true} />,
      },
      [CONFIG.tabs.reports.tasks]: {
        label: "Tasks",
        icon: <CheckSquare className="h-4 w-4" />,
        count: reportTasks?.length || 0, // ← Uses report data
        component: (
          <GenericTable
            data={getPaginatedData(reportTasks)} // ← Uses report data
            columns={reportsTasksColumns} // ← Enhanced columns
            rowKey="taskId"
            pagination={createPaginationConfig(reportTasks)}
            onPageChange={handlePageChange}
            emptyMessage="No tasks found"
          />
        ),
      },
      [CONFIG.tabs.reports.materials]: {
        label: "Materials",
        icon: <Package className="h-4 w-4" />,
        count: reportMaterials?.length || 0, // ← Uses report data
        component: (
          <GenericTable
            data={getPaginatedData(reportMaterials)} // ← Uses report data
            columns={reportsMaterialsColumns} // ← Enhanced columns
            rowKey="materialId"
            pagination={createPaginationConfig(reportMaterials)}
            onPageChange={handlePageChange}
            emptyMessage="No materials found"
          />
        ),
      },
      [CONFIG.tabs.reports.readings]: {
        label: "Readings",
        icon: <Wrench className="h-4 w-4" />,
        count: reportReadings?.length || 0, // ← Uses report data
        component: (
          <GenericTable
            data={getPaginatedData(reportReadings)} // ← Uses report data
            columns={reportsReadingsColumns} // ← Enhanced columns
            rowKey="readingId"
            pagination={createPaginationConfig(reportReadings)}
            onPageChange={handlePageChange}
            emptyMessage="No readings found"
          />
        ),
      },
      [CONFIG.tabs.reports.signature]: {
        label: "Signature",
        icon: <PenTool className="h-4 w-4" />,
        component: <SignatureSection />,
      },
      [CONFIG.tabs.reports.technicians]: {
        label: "Technicians",
        icon: <User className="h-4 w-4" />,
        component: (
          <GenericTable
            data={technicianDetails ? [technicianDetails] : []}
            columns={technicianColumns} // ← Your existing columns
            rowKey="fid"
            emptyMessage="No technician assigned"
          />
        ),
      },
    }),
    [
      reportTasks, // ← Report data
      reportMaterials, // ← Report data
      reportReadings, // ← Report data
      currentPage,
      technicianDetails,
      jobOverview,
    ]
  );

  // Consumer tab components configuration
  const consumerTabComponents = useMemo(
    () => ({
      [CONFIG.tabs.consumer.consumerMeter]: {
        label: "Consumer & Meter Details",
        icon: <User className="h-4 w-4" />,
        component: (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Consumer Details Section */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-green-600" />
                  Consumer Details
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      label: "Consumer Name",
                      key: "consumerName",
                      source: consumerDetails,
                    },
                    {
                      label: "Account No",
                      key: "accountNo",
                      source: consumerDetails,
                    },
                    {
                      label: "Phone",
                      key: "phone",
                      source: consumerDetails,
                      icon: <Phone className="h-4 w-4 text-gray-500" />,
                    },
                  ].map(({ label, key, source, icon }) => (
                    <div key={key}>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        {label}
                      </label>
                      <div className="text-gray-900 flex items-center gap-2">
                        {icon}
                        {source?.[key] || CONFIG.defaultValues.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Meter Details Section */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-green-600" />
                  Meter Details
                </h4>
                <div className="space-y-3">
                  {[
                    { label: "Meter No", key: "meterNo", source: meterDetails },
                    {
                      label: "Device No",
                      key: "deviceNo",
                      source: meterDetails,
                    },
                    {
                      label: "Premise",
                      key: "premise",
                      source: serviceOrderData,
                    },
                    {
                      label: "Physical Status",
                      key: "physicalStatus",
                      source: meterDetails,
                    },
                    {
                      label: "Assignment Status",
                      key: "assignmentStatus",
                      source: meterDetails,
                    },
                    {
                      label: "Reading Status",
                      key: "readingStatus",
                      source: meterDetails,
                    },
                  ].map(({ label, key, source }) => (
                    <div key={key}>
                      <label className="text-sm font-medium text-gray-500 block mb-1">
                        {label}
                      </label>
                      <div className="text-gray-900">
                        {source?.[key] || CONFIG.defaultValues.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ),
      },
      [CONFIG.tabs.consumer.technician]: {
        label: "Assigned Technician",
        icon: <User className="h-4 w-4" />,
        component: (
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-green-600" />
              Assignee Details
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: "Technician Name", key: "technicianName" },
                { label: "FID", key: "fid" },
                {
                  label: "Phone",
                  key: "phone",
                  icon: <Phone className="h-4 w-4 text-gray-500" />,
                },
                { label: "Role", key: "role.name" },
                {
                  label: "Email",
                  key: "email",
                  icon: <Mail className="h-4 w-4 text-gray-500" />,
                },
              ].map(({ label, key, icon }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {label}
                  </label>
                  <div className="flex items-start gap-2 text-gray-900">
                    {icon}
                    <span>
                      {technicianDetails?.[key] || CONFIG.defaultValues.text}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
    }),
    [consumerDetails, meterDetails, technicianDetails, serviceOrderData]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">🏢</span>
              <h1 className="text-lg font-semibold text-gray-900">
                Completed Service Order {id}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Completed Service Order -{" "}
                {serviceOrderData?.shortName || "Service Order"}
              </h1>
              <p className="text-gray-600">
                Completed service order with execution details and reports
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleApprove}
                disabled={acceptJobCard.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-2" />✓ Approve Service Order
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleReject}
                disabled={rejectJobCard.isPending}
              >
                <XCircle className="h-4 w-4 mr-2" />✗ Reject Service Order
              </Button>
              <Button
                onClick={handleDownload}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Report
              </Button>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <CompletedServiceOrderKPI
          jobOverview={jobOverview}
          serviceOrderData={serviceOrderData}
          className="mb-8"
        />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Service Order Details Card */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="text-xl font-semibold text-gray-800 text-center">
                Service Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: "SO Name", key: "soName" },
                  { label: "Short Name", key: "shortName" },
                  { label: "Created On", key: "createdOn" },
                  { label: "Assigned On", key: "assignedOn" },
                  { label: "Expected Completion", key: "expectedCompletion" },
                  { label: "Utility Services", key: "utilityServices" },
                  { label: "Area", key: "area" },
                  { label: "Subarea", key: "subarea" },
                  { label: "Premise", key: "premise" },
                  { label: "Tax Rate", key: "taxRate" },
                ].map(({ label, key }) => (
                  <div key={key} className="space-y-2">
                    <label className="text-sm font-medium text-gray-500 block">
                      {label}
                    </label>
                    <div className="text-gray-900 font-medium">
                      {serviceOrderData?.[key] || CONFIG.defaultValues.text}
                    </div>
                  </div>
                ))}

                {/* Full width fields */}
                {[
                  { label: "Description", key: "description" },
                  { label: "Remark", key: "remark" },
                ].map(({ label, key }) => (
                  <div key={key} className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-gray-500 block">
                      {label}
                    </label>
                    <div className="text-gray-900 font-medium ">
                      {serviceOrderData?.[key] || CONFIG.defaultValues.text}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                {currentServiceConfig.iconComponent}
                {currentServiceConfig.title} & Assigned Technician
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs
                defaultValue="service-details"
                idPrefix={`${validServiceType.toLowerCase()}-details-completed`}
                tabComponents={serviceTechnicianTabComponents}
                tabsListClassName="grid w-full grid-cols-2"
                disableUrlSync={true}
              />
            </CardContent>
          </Card>
        </div>

        {/* Job Requirements & Reports Card */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
            <CardTitle className="text-xl font-semibold">
              Job Requirements & Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-6">
              <div className="grid w-full grid-cols-2">
                <button
                  onClick={() => setActiveMainTab(CONFIG.tabs.main.details)}
                  className={`py-2 px-4 text-sm font-medium transition-colors ${
                    activeMainTab === CONFIG.tabs.main.details
                      ? "bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveMainTab(CONFIG.tabs.main.reports)}
                  className={`py-2 px-4 text-sm font-medium transition-colors ${
                    activeMainTab === CONFIG.tabs.main.reports
                      ? "bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Reports
                </button>
              </div>
            </div>
            <div className="min-h-[400px]">
              {/* ✅ KEEP: Details tab unchanged */}
              {activeMainTab === CONFIG.tabs.main.details && (
                <Tabs
                  defaultValue={CONFIG.tabs.details.overview}
                  idPrefix="details"
                  tabComponents={detailsTabComponents} // ← Uses existing data
                  tabsListClassName="grid w-full grid-cols-5"
                  disableUrlSync={true}
                />
              )}
              {/* ✅ NEW: Reports tab with API data */}
              {activeMainTab === CONFIG.tabs.main.reports && (
                <Tabs
                  defaultValue={CONFIG.tabs.reports.overview}
                  idPrefix="reports"
                  tabComponents={reportsTabComponents} // ← Uses report data
                  tabsListClassName="grid w-full grid-cols-6"
                  disableUrlSync={true}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetCompletedServiceOrderViewPage;
